type CreateFormation = {
	name: string; // eg 433
	positions: {
		x: number;
		y: number;
	}[];
};

type Position = { x: number; y: number };

// Formations not present in mockFormations (e.g. new formation codes used by
// newly promoted teams) fall back to positions generated from the digits of
// formationUsed, since that always excludes the keeper and its digits sum to 10.
function generateFormationPositions(formationUsed: string): Position[] | undefined {
	const lines = formationUsed.split('').map(Number);
	const isValid = lines.length > 0 && lines.every((n) => Number.isInteger(n) && n >= 1) && lines.reduce((a, b) => a + b, 0) === 10;

	if (!isValid) {
		return undefined;
	}

	const positions: Position[] = [{ x: 50, y: 5 }]; // Keeper

	lines.forEach((count, lineIndex) => {
		const y = 15 + (30 * lineIndex) / Math.max(1, lines.length - 1);
		for (let i = 0; i < count; i++) {
			const x = count === 1 ? 50 : 15 + (70 * i) / (count - 1);
			positions.push({ x, y });
		}
	});

	return positions;
}

export function getFormationPositions(formationUsed: string): Position[] | undefined {
	const preset = mockFormations.find((f) => f.name === formationUsed);
	return preset ? preset.positions : generateFormationPositions(formationUsed);
}

export const mockFormations: CreateFormation[] = [
	{
		name: '433',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 20, y: 20 }, // Left Back
			{ x: 40, y: 15 }, // Left Center Back
			{ x: 60, y: 15 }, // Right Center Back
			{ x: 80, y: 20 }, // Right Back

			{ x: 20, y: 30 }, // Left Center Midfielder
			{ x: 50, y: 28 }, // Defensive Midfielder
			{ x: 80, y: 30 }, // Right Center Midfielder

			{ x: 20, y: 40 }, // Left Forward
			{ x: 50, y: 42 }, // Center Forward
			{ x: 80, y: 40 }, // Right Forward
		],
	},
	{
		name: '442',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 25, y: 20 }, // Left Back
			{ x: 40, y: 15 }, // Left Center Back
			{ x: 60, y: 15 }, // Right Center Back
			{ x: 75, y: 20 }, // Right Back

			{ x: 20, y: 30 }, // Left Midfielder
			{ x: 50, y: 30 }, // Central Midfielder
			{ x: 80, y: 30 }, // Right Midfielder
			{ x: 50, y: 40 }, // Attacking Midfielder

			{ x: 35, y: 45 }, // Left Forward
			{ x: 65, y: 45 }, // Right Forward
		],
	},
	{
		name: '352',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 35, y: 15 }, // Left Center Back
			{ x: 50, y: 15 }, // Center Back
			{ x: 65, y: 15 }, // Right Center Back

			{ x: 20, y: 30 }, // Left Midfielder
			{ x: 40, y: 30 }, // Left Center Midfielder
			{ x: 50, y: 25 }, // Defensive Midfielder
			{ x: 60, y: 30 }, // Right Center Midfielder
			{ x: 80, y: 30 }, // Right Midfielder

			{ x: 40, y: 40 }, // Left Forward
			{ x: 60, y: 40 }, // Right Forward
		],
	},
	{
		name: '4231',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 15, y: 18 }, // Left Back
			{ x: 40, y: 15 }, // Left Center Back
			{ x: 60, y: 15 }, // Right Center Back
			{ x: 85, y: 18 }, // Right Back

			{ x: 30, y: 27 }, // Left Defensive Midfielder
			{ x: 70, y: 27 }, // Right Defensive Midfielder

			{ x: 20, y: 35 }, // Left Attacking Midfielder
			{ x: 50, y: 35 }, // Central Attacking Midfielder
			{ x: 80, y: 35 }, // Right Attacking Midfielder

			{ x: 50, y: 44 }, // Center Forward
		],
	},
	{
		name: '532',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 15, y: 20 }, // Left Wing Back
			{ x: 30, y: 15 }, // Left Center Back
			{ x: 50, y: 15 }, // Center Back
			{ x: 70, y: 15 }, // Right Center Back
			{ x: 85, y: 20 }, // Right Wing Back

			{ x: 25, y: 32 }, // Left Central Midfielder
			{ x: 50, y: 32 }, // Central Midfielder
			{ x: 75, y: 32 }, // Right Central Midfielder

			{ x: 40, y: 45 }, // Left Forward
			{ x: 60, y: 45 }, // Right Forward
		],
	},
	{
		name: '451',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 25, y: 20 }, // Left Back
			{ x: 40, y: 15 }, // Left Center Back
			{ x: 60, y: 15 }, // Right Center Back
			{ x: 75, y: 20 }, // Right Back

			{ x: 15, y: 40 }, // Left Midfielder
			{ x: 30, y: 32 }, // Left Central Midfielder
			{ x: 50, y: 30 }, // Defensive Midfielder
			{ x: 70, y: 32 }, // Right Central Midfielder
			{ x: 85, y: 40 }, // Right Midfielder

			{ x: 50, y: 45 }, // Center Forward
		],
	},
	{
		name: '343',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 35, y: 15 }, // Left Center Back
			{ x: 50, y: 15 }, // Center Back
			{ x: 65, y: 15 }, // Right Center Back

			{ x: 25, y: 30 }, // Left Midfielder
			{ x: 40, y: 25 }, // Left Center Midfielder
			{ x: 60, y: 25 }, // Right Center Midfielder
			{ x: 75, y: 30 }, // Right Midfielder

			{ x: 25, y: 40 }, // Left Forward
			{ x: 50, y: 40 }, // Center Forward
			{ x: 75, y: 40 }, // Right Forward
		],
	},
	{
		name: '4141',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 15, y: 20 }, // Left Back
			{ x: 40, y: 15 }, // Left Center Back
			{ x: 60, y: 15 }, // Right Center Back
			{ x: 85, y: 20 }, // Right Back

			{ x: 50, y: 25 }, // Defensive Midfielder

			{ x: 20, y: 40 }, // Left Midfielder
			{ x: 40, y: 35 }, // Left Center Midfielder
			{ x: 60, y: 35 }, // Right Center Midfielder
			{ x: 80, y: 40 }, // Right Midfielder

			{ x: 50, y: 45 }, // Center Forward
		],
	},
	{
		name: '3412',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 35, y: 15 }, // Left Center Back
			{ x: 50, y: 15 }, // Center Back
			{ x: 65, y: 15 }, // Right Center Back

			{ x: 25, y: 30 }, // Left Midfielder
			{ x: 40, y: 30 }, // Left Center Midfielder
			{ x: 60, y: 30 }, // Right Center Midfielder
			{ x: 75, y: 30 }, // Right Midfielder

			{ x: 50, y: 40 }, // Attacking Midfielder

			{ x: 40, y: 45 }, // Left Forward
			{ x: 60, y: 45 }, // Right Forward
		],
	},
	{
		name: '4222',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 25, y: 20 }, // Left Back
			{ x: 40, y: 15 }, // Left Center Back
			{ x: 60, y: 15 }, // Right Center Back
			{ x: 75, y: 20 }, // Right Back

			{ x: 35, y: 30 }, // Left Defensive Midfielder
			{ x: 65, y: 30 }, // Right Defensive Midfielder

			{ x: 30, y: 40 }, // Left Attacking Midfielder
			{ x: 70, y: 40 }, // Right Attacking Midfielder

			{ x: 40, y: 45 }, // Left Forward
			{ x: 60, y: 45 }, // Right Forward
		],
	},
	{
		name: '3421',
		positions: [
			{ x: 50, y: 5 }, // Keeper
			{ x: 30, y: 15 }, // Left Center Back
			{ x: 50, y: 15 }, // Center Back
			{ x: 70, y: 15 }, // Right Center Back

			{ x: 20, y: 30 }, // Left Midfielder
			{ x: 40, y: 25 }, // Left Center Midfielder
			{ x: 60, y: 25 }, // Right Center Midfielder
			{ x: 80, y: 30 }, // Right Midfielder

			{ x: 35, y: 40 }, // Left Attacking Midfielder
			{ x: 65, y: 40 }, // Right Attacking Midfielder

			{ x: 50, y: 48 }, // Center Forward
		],
	},
];
